import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import useAuth from '@/hooks/useAuth';
import usePlanOwner from '@/hooks/usePlanOwner';

import {
  createCurrentLanguageValue,
  getNameFromLanguage,
} from '@/utils/language';
import { isApiError } from '@/utils/request';

import GenericError from '@/components/Error/GenericError';

import { routes } from '@/constants';

import { LanguageObject } from '@/types';

import { OperatorDto } from '@/_generated/api-clients/xp';
import { clonePlan, getSystemPlans1Options } from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';
import CopyTemplatePopup from '@/routes/TrainingPlans/CopyTemplateModal/CopyTemplatePopup';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';

interface CopyTemplateProps {
  copiedTemplateId: string;
  setShowCopyTemplatePopup: (show: boolean) => void;
  setShowInvalidNameError: (show: boolean) => void;
  setShowTemplateModal: (show: boolean) => void;
  showCopyTemplatePopup: boolean;
}

const CopyTemplate: React.FC<CopyTemplateProps> = ({
  copiedTemplateId,
  setShowCopyTemplatePopup,
  setShowInvalidNameError,
  setShowTemplateModal,
  showCopyTemplatePopup,
}) => {
  const navigate = useNavigate();
  const track = useAmplitudeTrack();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const { isFetching, resolvePlanOwner: resolveOwner } = usePlanOwner();

  const { data } = useAuth();
  const allLocationsWithAtLeastLeaderPermissions =
    data?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const { data: templates, error: templatesError } = useQuery({
    ...getSystemPlans1Options(),
    enabled: allLocationsWithAtLeastLeaderPermissions.length > 0,
  });

  const isValidPlanName = useIsValidPlanName({
    onInvalidName: () => setShowInvalidNameError(true),
  });

  if (isApiError(templatesError)) {
    datadogRum.addError(
      templatesError instanceof Error
        ? templatesError
        : new Error(String(templatesError)),
    );
    return <GenericError />;
  }

  interface CopyPlanData {
    name: string;
    assignedOperator?: OperatorDto;
    locationNumber?: string;
  }

  const onCopyPlan = ({
    name,
    locationNumber,
    assignedOperator,
  }: CopyPlanData) => {
    const trimmedName = name.trim();
    const operatorId = assignedOperator?.id;
    const ownerPayload = resolveOwner({
      locationNumber,
      operatorId,
    });
    const ownerId = ownerPayload.locationNumber ?? ownerPayload.ownerId;

    if (!ownerId || !isValidPlanName(trimmedName, ownerId)) {
      return;
    }

    clonePlan({
      path: { planId: copiedTemplateId },
      body: {
        name: createCurrentLanguageValue(trimmedName, userLanguage),
        ...ownerPayload,
      },
    })
      .then(({ data }) => {
        const planId = data?.id ?? '';

        if (!planId) {
          datadogRum.addError(new Error('Missing cloned plan id'));
          return;
        }

        // fire amplitude event
        if (track !== null) {
          track('training_template_copied', {
            template_id: copiedTemplateId,
            operator: assignedOperator ?? 'empty',
          });
        }
        navigate(routes.plans.managePlansEdit({ planId }));
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };
  return (
    !isFetching && (
      <CopyTemplatePopup
        isOpen={showCopyTemplatePopup}
        planName={getNameFromLanguage(
          templates?.find(template => template.id === copiedTemplateId)
            ?.name as LanguageObject,
        )}
        reopenTemplates={() => setShowTemplateModal(true)}
        setOpen={setShowCopyTemplatePopup}
        submitHandler={onCopyPlan}
      />
    )
  );
};

export default CopyTemplate;
